import { createTheme } from '@mui/material/styles';

import { TextSize } from './contexts/LayoutPreferencesContext';

// Typography scale factors for the Text size preference. This is deliberately
// a separate axis from layout density: density tightens spacing only (a promise
// docs/60 USER_GUIDE.md makes explicitly), so shrinking type is opt-in rather
// than a side effect of choosing Compact.
//
// The factor is applied in two places because MUI needs both: `typography
// .fontSize` rescales every variant MUI derives itself (body1/body2/caption/
// subtitle/overline), while the h1-h6 and button entries below are explicit
// overrides and would otherwise stay pinned at their normal-size values.
export const TEXT_SIZE_SCALE: Record<TextSize, number> = {
  normal: 1,
  small: 0.875,
};

// MUI's own default; scaling it is what carries the derived variants.
const BASE_FONT_SIZE = 14;

const HEADING_REMS = {
  h1: 2.5,
  h2: 2,
  h3: 1.5,
  h4: 1.25,
  h5: 1.125,
  h6: 1,
} as const;

// Round to 3dp so the emitted rem values stay readable and stable in snapshots.
const scaleRem = (rem: number, scale: number): string =>
  `${Math.round(rem * scale * 1000) / 1000}rem`;

export const getTheme = (darkMode: boolean, textSize: TextSize = 'normal') => {
  const scale = TEXT_SIZE_SCALE[textSize] ?? TEXT_SIZE_SCALE.normal;

  return createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: '#3B82F6', // Blue
        light: '#60A5FA',
        dark: '#1E40AF',
      },
      secondary: {
        main: '#10B981', // Emerald
        light: '#34D399',
        dark: '#047857',
      },
      background: darkMode
        ? { default: '#0F172A', paper: '#1E293B' }
        : { default: '#F8FAFC', paper: '#FFFFFF' },
      text: darkMode
        ? { primary: '#F1F5F9', secondary: '#94A3B8' }
        : { primary: '#1E293B', secondary: '#64748B' },
    },
    typography: {
      fontFamily: 'Roboto, Arial, sans-serif',
      fontSize: BASE_FONT_SIZE * scale,
      h1: {
        fontSize: scaleRem(HEADING_REMS.h1, scale),
        fontWeight: 700,
      },
      h2: {
        fontSize: scaleRem(HEADING_REMS.h2, scale),
        fontWeight: 600,
      },
      h3: {
        fontSize: scaleRem(HEADING_REMS.h3, scale),
        fontWeight: 600,
      },
      h4: {
        fontSize: scaleRem(HEADING_REMS.h4, scale),
        fontWeight: 500,
      },
      h5: {
        fontSize: scaleRem(HEADING_REMS.h5, scale),
        fontWeight: 500,
      },
      h6: {
        fontSize: scaleRem(HEADING_REMS.h6, scale),
        fontWeight: 500,
      },
      button: {
        textTransform: 'none',
        fontWeight: 500,
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '8px 16px',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          },
        },
      },
    },
  });
};

const theme = getTheme(false);
export default theme;
